// app/frontend/pages/Users/ManagedEvents.tsx
import React, { useState, useEffect } from 'react';
import { Container, Text, Tabs, Grid, Group, Button, Loader, Center, Card, Box, Alert } from '@mantine/core';
import { showNotification, Notifications } from '@mantine/notifications';
import { Link, usePage, router } from '@inertiajs/react';
import { IconPlus, IconPencil, IconCalendarEvent, IconCalendarTime, IconNote } from '@tabler/icons-react';
import EventCard from '../../components/Cards/EventCard';
import RecurringEventCard from '../../components/Cards/RecurringEventCard';
import { StablePagination } from '../../components/StablePagination';
import { useScrollableTabs } from '../../src/hooks/useScrollableTabs';
import '../../styles/ScrollTabs.css';
import { Stack } from 'tabler-icons-react';

// Interfaces based on the ERB and controller code
interface Event {
  id: number;
  name: string;
  slug: string;
  date_created: string;
  event_start_date: string;
  event_end_date: string;
  venue_name: string;
  status: string;
  description: string;
  country?: {
    name: string;
    flag_emoji: string;
  };
  // Add any other event properties you need
  [key: string]: any;
}

interface RecurringEvent {
  id: number;
  name: string;
  slug: string;
  num_occurrences: number;
  frequency: string;
  day_of_week: string;
  venue_name: string;
  description: string;
  country?: {
    name: string;
    flag_emoji: string;
  };
  // Add any other recurring event properties
  [key: string]: any;
}

interface DraftEvent {
  id: number;
  name: string;
  description?: string;
  // Add other draft event properties
  [key: string]: any;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total_pages: number;
}

interface ManagedEventsProps {
  user: {
    id: number;
    username: string;
    superadmin?: boolean;
  };
  // These will be selectively loaded based on active tab
  approved_events?: Event[];
  pending_events?: Event[];
  recurring_events?: RecurringEvent[];
  draft_events?: DraftEvent[];
  // Pagination data for each tab
  approved_pagination?: Pagination;
  pending_pagination?: Pagination;
  recurring_pagination?: Pagination;
  draft_pagination?: Pagination;
  // Initial active tab from server
  active_tab?: string;
  // Metadata counts are always available
  metadata?: {
    approved_count: number;
    pending_count: number;
    recurring_count: number;
    draft_count: number;
  };
}

const ManagedEvents: React.FC<ManagedEventsProps> = ({
  user,
  approved_events,
  pending_events,
  recurring_events,
  draft_events,
  approved_pagination,
  pending_pagination,
  recurring_pagination,
  draft_pagination,
  active_tab = 'approved',
  metadata,
}) => {
  // Tab state - use active_tab from props for initial state
  const [activeTab, setActiveTab] = useState<string>(active_tab);
  const page = usePage();

  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination states
  const [approvedCurrentPage, setApprovedCurrentPage] = useState(approved_pagination?.current_page || 1);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(pending_pagination?.current_page || 1);
  const [recurringCurrentPage, setRecurringCurrentPage] = useState(recurring_pagination?.current_page || 1);
  const [draftCurrentPage, setDraftCurrentPage] = useState(draft_pagination?.current_page || 1);

  // Scrollable tabs hooks
  const { tabListReference: mobileTabListReference } = useScrollableTabs();
  const { tabListReference: desktopTabListReference } = useScrollableTabs();

  // Date formatting helper function
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }

      // Format the date - you can customize this format
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      // Alternative simpler format
      // return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date error';
    }
  };

  // console.log('ManagedEvents received props:', {
  //   user,
  //   approved_events: approved_events
  //     ? {
  //         length: approved_events.length,
  //         first: approved_events[0],
  //         isArray: Array.isArray(approved_events),
  //       }
  //     : 'undefined',
  //   pending_events: pending_events
  //     ? {
  //         length: pending_events.length,
  //         first: pending_events[0],
  //         isArray: Array.isArray(pending_events),
  //       }
  //     : 'undefined',
  //   recurring_events: recurring_events
  //     ? {
  //         length: recurring_events.length,
  //         first: recurring_events[0],
  //         isArray: Array.isArray(recurring_events),
  //       }
  //     : 'undefined',
  //   draft_events: draft_events
  //     ? {
  //         length: draft_events.length,
  //         first: draft_events[0],
  //         isArray: Array.isArray(draft_events),
  //       }
  //     : 'undefined',
  //   approved_pagination,
  //   pending_pagination,
  //   recurring_pagination,
  //   draft_pagination,
  //   active_tab,
  //   metadata,
  // });

  // // Debug log props
  // console.log('ManagedEvents Props:', {
  //   user,
  //   activeTab,
  //   approved_events: approved_events?.length,
  //   pending_events: pending_events?.length,
  //   recurring_events: recurring_events?.length,
  //   draft_events: draft_events?.length,
  //   metadata,
  // });

  // Handle tab change without page refresh
  const handleTabChange = (tab: string | null) => {
    if (!tab) return;

    console.log('🔄🔄🔄🔄🔄 Tab changing to:', tab);
    setActiveTab(tab);
    setIsLoading(true);

    // Define which props to fetch for each tab
    const onlyProps = ['active_tab', 'metadata', 'managed_events'];

    if (tab === 'approved') {
      onlyProps.push('approved_events', 'approved_pagination');
    } else if (tab === 'pending') {
      onlyProps.push('pending_events', 'pending_pagination');
    } else if (tab === 'recurring') {
      onlyProps.push('recurring_events', 'recurring_pagination');
    } else if (tab === 'draft') {
      onlyProps.push('draft_events', 'draft_pagination');
    }

    // Fetch data for the selected tab
    router.visit(`/users/${user.username}/managed-events`, {
      data: {
        active_tab: tab,
      },
      preserveState: true,
      preserveScroll: true,
      only: onlyProps,
      onFinish: () => setIsLoading(false),
    });
  };

  // Handle page change for each tab
  const handleApprovedPageChange = (page: number) => {
    setIsLoading(true);
    setApprovedCurrentPage(page);

    router.visit(`/users/${user.username}/managed-events`, {
      data: {
        active_tab: 'approved',
        approved_page: page,
      },
      preserveState: true,
      preserveScroll: true,
      only: ['approved_events', 'approved_pagination', 'metadata', 'managed_events'],
      onFinish: () => setIsLoading(false),
    });
  };

  const handlePendingPageChange = (page: number) => {
    setIsLoading(true);
    setPendingCurrentPage(page);

    router.visit(`/users/${user.username}/managed-events`, {
      data: {
        active_tab: 'pending',
        pending_page: page,
      },
      preserveState: true,
      preserveScroll: true,
      only: ['pending_events', 'pending_pagination', 'metadata', 'managed_events'],
      onFinish: () => setIsLoading(false),
    });
  };

  const handleRecurringPageChange = (page: number) => {
    setIsLoading(true);
    setRecurringCurrentPage(page);

    router.visit(`/users/${user.username}/managed-events`, {
      data: {
        active_tab: 'recurring',
        recurring_page: page,
      },
      preserveState: true,
      preserveScroll: true,
      only: ['recurring_events', 'recurring_pagination', 'metadata', 'managed_events'],
      onFinish: () => setIsLoading(false),
    });
  };

  const handleDraftPageChange = (page: number) => {
    setIsLoading(true);
    setDraftCurrentPage(page);

    router.visit(`/users/${user.username}/managed-events`, {
      data: {
        active_tab: 'draft',
        draft_page: page,
      },
      preserveState: true,
      preserveScroll: true,
      only: ['draft_events', 'draft_pagination', 'metadata', 'managed_events'],
      onFinish: () => setIsLoading(false),
    });
  };

  // Event click handler
  const handleEventClick = (event: Event) => {
    // Navigate to event page
    window.location.href = `/events/${event.slug}`;
  };

  // Recurring event click handler
  const handleRecurringEventClick = (recurringEvent: RecurringEvent) => {
    // Navigate to recurring event page
    window.location.href = `/recurring_events/${recurringEvent.slug}`;
  };

  // Draft event edit handler
  const handleEditDraft = (draftId: number) => {
    window.location.href = `/suggest_event?edit_draft=${draftId}`;
  };

  // Show different tabs based on metadata
  const showPendingTab = (metadata?.pending_count ?? 0) > 0;
  const showRecurringTab = (metadata?.recurring_count ?? 0) > 0;
  const showDraftTab = (metadata?.draft_count ?? 0) > 0;

  // Add this right after the formatDate function
  useEffect(() => {
    if (draft_events && draft_events.length > 0) {
      console.log('Draft Events Complete Object:', draft_events[0]);

      // Look for any timestamp-like fields in the object
      const timestampFields = Object.entries(draft_events[0]).filter(
        ([key, value]) =>
          key.includes('date') || key.includes('time') || key.includes('created') || key.includes('updated'),
      );

      console.log('Potential timestamp fields:', timestampFields);
    }
  }, [draft_events]);

  return (
    <Container
      style={{
        padding: '0 8px',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        minHeight: '80vh',
      }}
    >
      <Notifications position="top-right" />

      <Group justify="space-between" mb="md">
        <Text
          size="2rem"
          fw={700}
          mb="1rem"
          className="us-settings-header my-3"
          style={{ color: '#EA580C', fontSize: '1.2rem' }}
        >
          Events You Manage
        </Text>

        {/* Add button as a separate floating element */}
        <Button
          component="a"
          href="/suggest_event"
          leftSection={<IconPlus size={16} />}
          variant="filled"
          color="orange"
        >
          Add New Event
        </Button>
      </Group>
      <Text className="us-settings-header mb-4">Manage your events, see their status, and create new ones.</Text>

      {error && (
        <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tabs.List ref={mobileTabListReference} className="scrollable-tabs-list">
          <Tabs.Tab value="approved" className="scrollable-tab" leftSection={<IconCalendarEvent size={16} />}>
            Approved Events ({metadata?.approved_count || 0})
          </Tabs.Tab>

          {showPendingTab && (
            <Tabs.Tab value="pending" className="scrollable-tab" leftSection={<IconCalendarTime size={16} />}>
              Pending Events ({metadata?.pending_count || 0})
            </Tabs.Tab>
          )}

          {showRecurringTab && (
            <Tabs.Tab value="recurring" className="scrollable-tab" leftSection={<IconCalendarTime size={16} />}>
              Recurring Events ({metadata?.recurring_count || 0})
            </Tabs.Tab>
          )}

          {showDraftTab && (
            <Tabs.Tab value="draft" className="scrollable-tab" leftSection={<IconNote size={16} />}>
              Draft Events ({metadata?.draft_count || 0})
            </Tabs.Tab>
          )}
        </Tabs.List>

        {/* Approved Events Tab */}
        <Tabs.Panel value="approved" pt="md">
          {isLoading ? (
            <Center my="xl">
              <Loader color="#EA580C" size="lg" />
            </Center>
          ) : approved_events && approved_events.length > 0 ? (
            <>
              <Group justify="flex-start" mb="md">
                <Text className="text-white">
                  Showing {approved_events.length} of {metadata?.approved_count || 0} approved events
                </Text>
              </Group>

              {approved_pagination && approved_pagination.total_pages > 1 && (
                <Group justify="flex-start" mb="md">
                  <StablePagination
                    total={approved_pagination.total_pages}
                    value={approvedCurrentPage}
                    onChange={handleApprovedPageChange}
                    color="#EA580C"
                  />
                </Group>
              )}

              <Grid mb="lg">
                {approved_events.map((event) => (
                  <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={event.id}>
                    <EventCard event={event} onCardClick={() => handleEventClick(event)} showManageButton={true} />
                  </Grid.Col>
                ))}
              </Grid>

              {approved_pagination && approved_pagination.total_pages > 1 && (
                <Group justify="flex-start" mb="md">
                  <StablePagination
                    total={approved_pagination.total_pages}
                    value={approvedCurrentPage}
                    onChange={handleApprovedPageChange}
                    color="#EA580C"
                  />
                </Group>
              )}
            </>
          ) : (
            <Text ta="left" my="xl" className="text-white">
              You don't have any approved events yet. Create an event using the 'Add New Event' button.
            </Text>
          )}
        </Tabs.Panel>

        {/* Pending Events Tab */}
        {showPendingTab && (
          <Tabs.Panel value="pending" pt="md">
            {isLoading ? (
              <Center my="xl">
                <Loader color="#EA580C" size="lg" />
              </Center>
            ) : pending_events && pending_events.length > 0 ? (
              <>
                <Group justify="flex-start" mb="md">
                  <Text className="text-white">
                    Showing {pending_events.length} of {metadata?.pending_count || 0} pending events
                  </Text>
                </Group>

                {pending_pagination && pending_pagination.total_pages > 1 && (
                  <Group justify="flex-start" mb="md">
                    <StablePagination
                      total={pending_pagination.total_pages}
                      value={pendingCurrentPage}
                      onChange={handlePendingPageChange}
                      color="#EA580C"
                    />
                  </Group>
                )}

                <Grid mb="lg">
                  {pending_events.map((event) => (
                    <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={event.id}>
                      <EventCard
                        event={event}
                        onCardClick={() => handleEventClick(event)}
                        showManageButton={true}
                        pendingBadge={true}
                      />
                    </Grid.Col>
                  ))}
                </Grid>

                {pending_pagination && pending_pagination.total_pages > 1 && (
                  <Group justify="flex-start" mb="md">
                    <StablePagination
                      total={pending_pagination.total_pages}
                      value={pendingCurrentPage}
                      onChange={handlePendingPageChange}
                      color="#EA580C"
                    />
                  </Group>
                )}
              </>
            ) : (
              <Text ta="left" my="xl" className="text-white">
                You don't have any pending events at this time.
              </Text>
            )}
          </Tabs.Panel>
        )}

        {/* Recurring Events Tab */}
        {showRecurringTab && (
          <Tabs.Panel value="recurring" pt="md">
            {isLoading ? (
              <Center my="xl">
                <Loader color="#EA580C" size="lg" />
              </Center>
            ) : recurring_events && recurring_events.length > 0 ? (
              <>
                <Group justify="flex-start" mb="md">
                  <Text className="text-white">
                    Showing {recurring_events.length} of {metadata?.recurring_count || 0} recurring events
                  </Text>
                </Group>

                {recurring_pagination && recurring_pagination.total_pages > 1 && (
                  <Group justify="flex-start" mb="md">
                    <StablePagination
                      total={recurring_pagination.total_pages}
                      value={recurringCurrentPage}
                      onChange={handleRecurringPageChange}
                      color="#EA580C"
                    />
                  </Group>
                )}

                <Grid mb="lg">
                  {recurring_events.map((recurringEvent) => (
                    <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={recurringEvent.id}>
                      <RecurringEventCard recurringEvent={recurringEvent} user={user} />
                    </Grid.Col>
                  ))}
                </Grid>

                {recurring_pagination && recurring_pagination.total_pages > 1 && (
                  <Group justify="flex-start" mb="md">
                    <StablePagination
                      total={recurring_pagination.total_pages}
                      value={recurringCurrentPage}
                      onChange={handleRecurringPageChange}
                      color="#EA580C"
                    />
                  </Group>
                )}
              </>
            ) : (
              <Text ta="left" my="xl" className="text-white">
                You don't have any recurring events set up yet.
              </Text>
            )}
          </Tabs.Panel>
        )}

        {/* Draft Events Tab */}
        {showDraftTab && (
          <Tabs.Panel value="draft" pt="md">
            {isLoading ? (
              <Center my="xl">
                <Loader color="#EA580C" size="lg" />
              </Center>
            ) : draft_events && draft_events.length > 0 ? (
              <>
                <Group justify="flex-start" mb="md">
                  <Text className="text-white">
                    Showing {draft_events.length} of {metadata?.draft_count || 0} draft events
                  </Text>
                </Group>

                {draft_pagination && draft_pagination.total_pages > 1 && (
                  <Group justify="flex-start" mb="md">
                    <StablePagination
                      total={draft_pagination.total_pages}
                      value={draftCurrentPage}
                      onChange={handleDraftPageChange}
                      color="#EA580C"
                    />
                  </Group>
                )}

                {draft_events
                  .slice() // Create a copy to avoid mutating the original array
                  .sort((a, b) => {
                    // Sort by date_created in descending order (newest first)
                    return new Date(b.date_created).getTime() - new Date(a.date_created).getTime();
                  })
                  .map((draft) => (
                    <Box
                      key={draft.id}
                      mb="md"
                      p="md"
                      style={(theme) => ({
                        backgroundColor: '#f2f2f240',
                        borderRadius: theme.radius.md,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      })}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Text fw={600} c="white" style={{ lineHeight: '1rem' }}>
                          {draft.name}
                        </Text>
                        <Text c="#f2f2f290" size="xs" style={{ fontSize: '0.7rem', lineHeight: '1rem' }}>
                          Created on: {draft.date_created ? formatDate(draft.date_created) : 'No date available'}
                        </Text>
                      </div>
                      <Button
                        variant="filled"
                        color="orange"
                        leftSection={<IconPencil size={16} />}
                        onClick={() => handleEditDraft(draft.id)}
                      >
                        Edit
                      </Button>
                    </Box>
                  ))}

                {draft_pagination && draft_pagination.total_pages > 1 && (
                  <Group justify="flex-start" mb="md">
                    <StablePagination
                      total={draft_pagination.total_pages}
                      value={draftCurrentPage}
                      onChange={handleDraftPageChange}
                      color="#EA580C"
                    />
                  </Group>
                )}
              </>
            ) : (
              <Text ta="left" my="xl" className="text-white">
                You don't have any draft events.
              </Text>
            )}
          </Tabs.Panel>
        )}
      </Tabs>
    </Container>
  );
};

export default ManagedEvents;
